import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  Home, Send, ArrowLeftRight, History, Users, Wallet, Gift, BadgeCheck,
  LockKeyhole, ShieldCheck, Plus, Search, Bell, Menu, X, MessageCircle,
  LayoutDashboard, ClipboardList, UserCog, AlertTriangle, Lock, Shield, User,
} from 'lucide-react';
import { useAuthStore, useUIStore } from '../../store';
import ImpersonationBanner from '../shared/ImpersonationBanner';
import SupportChat from '../shared/SupportChat';
import TxDetailModal from '../shared/TxDetailModal';

const CLIENT_NAV = [
  { to: '/app/dashboard',  label: 'Inicio',         Icon: Home },
  { to: '/app/enviar',     label: 'Enviar dinero',  Icon: Send },
  { to: '/app/p2p',        label: 'Mercado P2P',    Icon: ArrowLeftRight },
  { to: '/app/historial',  label: 'Mis transacciones', Icon: History },
  { to: '/app/contactos',  label: 'Destinatarios',  Icon: Users },
  { to: '/app/wallet',     label: 'Pagos',          Icon: Wallet },
  { to: '/app/giftcards',  label: 'Gift Cards',     Icon: Gift },
  { to: '/app/kyc',        label: 'Verificación',   Icon: BadgeCheck },
  { to: '/app/2fa',        label: '2FA Seguridad',  Icon: LockKeyhole },
  { to: '/app/agentes',    label: 'Agentes verificados', Icon: ShieldCheck },
  { to: '/app/agente',     label: 'Ser agente',     Icon: Plus },
];

const ADMIN_NAV = [
  { to: '/app/admin',            label: 'Overview',   Icon: LayoutDashboard },
  { to: '/app/admin/txs',        label: 'Órdenes',    Icon: ClipboardList },
  { to: '/app/admin/agentes',    label: 'Agentes',    Icon: Users },
  { to: '/app/admin/usuarios',   label: 'Usuarios',   Icon: UserCog },
  { to: '/app/admin/compliance', label: 'Compliance', Icon: AlertTriangle },
  { to: '/app/admin/escrow',     label: 'Escrow',     Icon: Lock },
  { to: '/app/admin/trust',      label: 'Confianza',  Icon: Shield },
];

const BOTTOM_NAV_CLIENT = [
  { to: '/app/dashboard',  label: 'Inicio',    Icon: Home },
  { to: '/app/enviar',     label: 'Enviar',    Icon: Send },
  { to: '/app/p2p',        label: 'P2P',       Icon: ArrowLeftRight },
  { to: '/app/historial',  label: 'Historial', Icon: History },
  { to: '/app/wallet',     label: 'Pagos',     Icon: Wallet },
  { to: '/app/kyc',        label: 'Perfil',    Icon: User },
];

const BOTTOM_NAV_ADMIN = [
  { to: '/app/admin',            label: 'Overview', Icon: LayoutDashboard },
  { to: '/app/admin/txs',        label: 'Órdenes',  Icon: ClipboardList },
  { to: '/app/admin/agentes',    label: 'Agentes',  Icon: Users },
  { to: '/app/admin/usuarios',   label: 'Usuarios', Icon: UserCog },
  { to: '/app/admin/compliance', label: 'Alertas',  Icon: AlertTriangle },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function AppShell() {
  const { user, role, logout, impersonating } = useAuthStore();
  const [showSupport, setShowSupport] = React.useState(false);
  const { txDetailRef } = useUIStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const isAdmin = (role || '').toLowerCase() === 'admin';
  const bottomNav = isAdmin ? BOTTOM_NAV_ADMIN : BOTTOM_NAV_CLIENT;

  const handleNavClick = () => { if (isMobile) setSidebarOpen(false); };

  useEffect(() => {
    if (isMobile && sidebarOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, sidebarOpen]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleSearch = (e) => {
    if (e.key === 'Enter' && search.trim()) {
      navigate(`/app/historial?q=${encodeURIComponent(search.trim())}`);
      setSearch('');
      if (isMobile) setSidebarOpen(false);
    }
  };

  const linkStyle = ({ isActive }) => ({
    display: 'flex', alignItems: 'center', gap: 13,
    margin: '0 11px', padding: '12px 15px',
    textDecoration: 'none', borderRadius: 12,
    color: isActive ? '#fff' : '#afb0bf',
    fontSize: 13, fontWeight: isActive ? 700 : 500,
    background: isActive ? 'var(--nav-active-grad)' : 'transparent',
    transition: 'all .15s',
  });

  const sidebarVisible = !isMobile || sidebarOpen;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', position: 'relative', paddingTop: impersonating ? 36 : 0 }}>
      <ImpersonationBanner />
      <style>{`
        @media (max-width: 768px) {
          .main-area { margin-left: 0 !important; padding-bottom: 72px !important; }
          .topbar-send { display: none !important; }
          .topbar-search { display: none !important; }
          .topbar-userchip { display: none !important; }
          .hamburger-btn { display: flex !important; }
          .bottom-nav { display: flex !important; }
          .page-pad { padding: 16px !important; }
          .grid-4 { grid-template-columns: 1fr 1fr !important; }
          .grid-2 { grid-template-columns: 1fr !important; }
          table { display: block; overflow-x: auto; -webkit-overflow-scrolling: touch; }
          input, select, textarea { font-size: 16px !important; }
        }
        @media (min-width: 769px) {
          .bottom-nav { display: none !important; }
          .hamburger-btn { display: none !important; }
          .mobile-logo { display: none !important; }
        }
        @media (max-width: 480px) {
          .grid-4 { grid-template-columns: 1fr !important; }
        }
        .sidebar-nav a:hover { background: #272730; color: #fff; }
        .nav-bottom-link {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          flex: 1; padding: 6px 4px; text-decoration: none;
          color: #8e8f9c; font-size: 10px; gap: 3px;
          transition: color .15s; min-height: 44px;
        }
        .nav-bottom-link.active { color: #ff6a2a; }
        .topbar-search input::placeholder { color: #9c9daa; }
      `}</style>

      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 299 }} />
      )}

      {/* Sidebar */}
      {sidebarVisible && (
        <aside style={{
          width: 245, flexShrink: 0, background: 'var(--sidebar-bg)',
          position: 'fixed', top: 0, left: 0, bottom: 0,
          display: 'flex', flexDirection: 'column',
          zIndex: isMobile ? 300 : 200, overflowY: 'auto',
          boxShadow: isMobile ? '4px 0 24px rgba(0,0,0,.4)' : 'none',
        }}>
          {/* Brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '24px 20px 26px' }}>
            <div style={{
              width: 41, height: 41, borderRadius: 13, background: 'var(--brand-grad)',
              display: 'grid', placeItems: 'center',
              fontFamily: 'Outfit', fontSize: 24, fontWeight: 800, color: '#fff',
            }}>G</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Outfit', fontSize: 21, fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>Guara<span style={{ color: 'var(--emerald)' }}>po</span></div>
              <div style={{ fontSize: 8, letterSpacing: 1, color: '#9c9daa', textTransform: 'uppercase' }}>
                {isAdmin ? 'Administración' : 'Entrega de efectivo en Cuba'}
              </div>
            </div>
            {isMobile && (
              <button onClick={() => setSidebarOpen(false)}
                style={{ background: 'none', border: 'none', color: '#9c9daa', cursor: 'pointer', padding: 4 }}>
                <X size={20} />
              </button>
            )}
          </div>

          {/* Nav */}
          <nav className="sidebar-nav" style={{ padding: '0 0 10px', flex: 1, display: 'grid', gap: 6, alignContent: 'start' }}>
            {!isAdmin ? (
              CLIENT_NAV.map(({ to, label, Icon }) => (
                <NavLink key={to} to={to} style={linkStyle} onClick={handleNavClick}>
                  <Icon size={18} /> {label}
                </NavLink>
              ))
            ) : (
              <>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: '#5e5f6c', padding: '4px 26px' }}>
                  Mi cuenta
                </div>
                {CLIENT_NAV.map(({ to, label, Icon }) => (
                  <NavLink key={to} to={to} style={linkStyle} onClick={handleNavClick}>
                    <Icon size={18} /> {label}
                  </NavLink>
                ))}
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: '#c2573f', padding: '14px 26px 4px', marginTop: 8, borderTop: '1px solid #24252e' }}>
                  Administración
                </div>
                {ADMIN_NAV.map(({ to, label, Icon }) => (
                  <NavLink key={to} to={to} style={linkStyle} onClick={handleNavClick}>
                    <Icon size={18} /> {label}
                  </NavLink>
                ))}
              </>
            )}
          </nav>

          {/* Claim + user */}
          <div style={{ padding: 20, background: 'linear-gradient(180deg, transparent, #24212a)' }}>
            <div style={{
              height: 86, borderRadius: 14, marginBottom: 12,
              background: 'linear-gradient(135deg, #fd5a2a66, #0d1733), var(--brand-grad)',
              display: 'flex', alignItems: 'flex-end', padding: 12,
            }}>
              <span style={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: 15, color: '#fff', lineHeight: 1.2 }}>
                Más cerca<br />de los tuyos
              </span>
            </div>
            <p style={{ fontSize: 11.5, color: '#bfc0c8', margin: '0 0 14px', lineHeight: 1.5 }}>
              Entrega puerta a puerta, protegida por escrow.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', background: 'var(--brand-grad)',
                display: 'grid', placeItems: 'center',
                fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0,
              }}>
                {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.full_name || user?.email || 'Usuario'}
                </div>
                <div style={{ fontSize: 10, color: '#9c9daa' }}>{isAdmin ? 'Admin' : 'Cliente verificado'}</div>
              </div>
            </div>
            <button onClick={handleLogout} style={{
              width: '100%', padding: 8, borderRadius: 10, border: 'none',
              background: '#24252e', color: '#afb0bf',
              fontSize: 11, cursor: 'pointer', minHeight: 36,
            }}>Cerrar sesión</button>
          </div>
        </aside>
      )}

      {/* Main */}
      <main className="main-area" style={{
        marginLeft: isMobile ? 0 : 245,
        flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh',
      }}>
        {/* Topbar */}
        <div style={{
          background: '#fff', borderBottom: '1px solid var(--card-border)',
          height: 62, padding: '0 22px', gap: 14,
          display: 'flex', alignItems: 'center',
          position: 'sticky', top: 0, zIndex: 100,
        }}>
          <button className="hamburger-btn" onClick={() => setSidebarOpen(true)}
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink)', padding: 4, minHeight: 44, minWidth: 44, alignItems: 'center', justifyContent: 'center' }}>
            <Menu size={22} />
          </button>
          <div className="mobile-logo" style={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: 18 }}>Guara<span style={{ color: 'var(--emerald)' }}>po</span></div>

          <div className="topbar-search" style={{
            flex: 1, maxWidth: 460, display: 'flex', alignItems: 'center', gap: 9,
            padding: '9px 15px', borderRadius: 999,
            border: '1px solid var(--card-border)', background: 'var(--bg)',
          }}>
            <Search size={17} color="#9c9daa" />
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleSearch}
              placeholder="Buscar transacción, destinatario, agente..."
              style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13, outline: 'none', color: 'var(--ink)' }} />
          </div>

          <div style={{ flex: 1 }} />

          <button className="icon-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5e5e70', padding: 6 }}>
            <Bell size={19} />
          </button>

          <button className="topbar-send g-primary" onClick={() => navigate('/app/enviar')} style={{ padding: '10px 20px' }}>
            Enviar dinero
          </button>

          <div className="topbar-userchip" style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', background: 'var(--brand-grad)',
              display: 'grid', placeItems: 'center',
              fontSize: 13, fontWeight: 700, color: '#fff',
            }}>
              {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.2 }}>
                {user?.full_name?.split(' ')[0] || 'Usuario'}
              </div>
              <div style={{ fontSize: 10, color: '#9c9daa' }}>{isAdmin ? 'Admin' : 'Cliente verificado'}</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="page-pad" style={{ padding: 26, flex: 1 }}>
          <Outlet />
        </div>
      </main>

      {/* Bottom nav */}
      <nav className="bottom-nav" style={{
        display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'var(--sidebar-bg)', borderTop: '1px solid #24252e',
        height: 64, zIndex: 200, paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        {bottomNav.map(({ to, label, Icon }) => (
          <NavLink key={to} to={to}
            className={({ isActive }) => `nav-bottom-link${isActive ? ' active' : ''}`}>
            <Icon size={19} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Modals */}
      {txDetailRef && <TxDetailModal ref_={txDetailRef} />}
      {!showSupport && (
        <button onClick={() => setShowSupport(true)} style={{
          position: 'fixed', bottom: 24, right: 24, width: 52, height: 52, borderRadius: '50%',
          background: 'var(--orange-grad)', border: 'none', color: '#fff', cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(232,73,29,.4)', zIndex: 999,
          display: 'grid', placeItems: 'center',
        }}>
          <MessageCircle size={22} />
        </button>
      )}
      {showSupport && <SupportChat onClose={() => setShowSupport(false)} />}
    </div>
  );
}
